import React, { useState } from 'react';
import {
  Alert,
  Button,
  NumberInput,
  SimpleGrid,
  Stack,
  Text,
  Textarea,
  Title,
} from '@mantine/core';
import {
  useLoaderData,
  useRevalidator,
  useRouteLoaderData,
} from 'react-router-dom';
import { z } from 'zod';
import {
  WorkflowPolicyViewSchema,
  type Me,
  type WorkflowPolicy,
} from '@tinypro-audit/contracts';
import { fetchJson } from '../api/client';

export function PurchasingPolicyPage() {
  const data = useLoaderData() as z.infer<typeof WorkflowPolicyViewSchema>;
  const { me } = useRouteLoaderData('root') as { me: Me };
  const revalidator = useRevalidator();
  const [settings, setSettings] = useState(data.settings);
  const [version, setVersion] = useState(data.version);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const hours: [Exclude<keyof WorkflowPolicy, 'approved_rules'>, string][] = [
    ['acknowledgement_due_hours', 'Acknowledgement due after PO submission'],
    ['response_due_hours', 'Response due after exception opens'],
    ['escalation_after_hours', 'Escalate after response deadline'],
    ['collection_interval_hours', 'Revisit accepted projects'],
    ['review_timeout_hours', 'Review completion timeout'],
  ];
  const save = async () => {
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const saved = await fetchJson(
        '/api/projects/workflow/policy',
        {
          method: 'PUT',
          body: JSON.stringify({ version, settings }),
        },
        WorkflowPolicyViewSchema.extend({ queued_runs: z.number() }),
      );
      setVersion(saved.version);
      setSettings(saved.settings);
      setMessage(
        `Policy version ${saved.version} saved. ${saved.queued_runs} purchasing reviews queued.`,
      );
      await revalidator.revalidate();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Request failed.');
    } finally {
      setBusy(false);
    }
  };
  if (me.role !== 'admin')
    return (
      <Alert color="yellow">
        Only administrators can manage review policies.
      </Alert>
    );
  return (
    <Stack maw={1000}>
      <Title order={2}>Purchasing review policy</Title>
      <Text c="dimmed">
        Version {version}. These rules apply to PO vs. approved estimate and
        manufacturer acknowledgement reviews. Pre-site form reviews use their
        existing review rules.
      </Text>
      {error && <Alert color="red">{error}</Alert>}
      {message && <Alert color="green">{message}</Alert>}
      <Textarea
        label="Approved purchasing rules"
        description="Manufacturer mappings, required fields, equivalences, severity and routing. Use stable rule IDs so reviews can cite them."
        minRows={8}
        autosize
        maxLength={40000}
        value={settings.approved_rules}
        onChange={(e) =>
          setSettings({ ...settings, approved_rules: e.currentTarget.value })
        }
      />
      <Text size="sm">
        Leave deadlines blank until approved. Alerts and escalation appear in
        Inbox.
      </Text>
      <SimpleGrid cols={{ base: 1, md: 2 }}>
        {hours.map(([key, title]) => (
          <NumberInput
            key={key}
            label={`${title} (hours)`}
            min={1}
            max={8760}
            allowDecimal={false}
            value={settings[key] ?? ''}
            onChange={(value) =>
              setSettings((current) => ({
                ...current,
                [key]: typeof value === 'number' ? value : null,
              }))
            }
          />
        ))}
      </SimpleGrid>
      <Text size="sm">
        Saving queues a new purchasing review for each project with accepted
        evidence. Original documents, pre-site results and prior policy versions
        remain in history.
      </Text>
      <Button
        loading={busy}
        onClick={() => void save()}
        style={{ alignSelf: 'start' }}
      >
        Save policy and recheck purchasing reviews
      </Button>
    </Stack>
  );
}
